import React, { useState } from "react";
import {
  Mail, Phone, MapPin, MessageCircle, Send, Clock, ArrowRight, CalendarClock,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BRAND } from "../lib/mockData";

const INQUIRY_TYPES = ["Patent filing", "Concierge / premium services", "Enterprise / B2B partnership", "Become an attorney partner", "Press / media", "Other"];

export default function Contact() {
  const [type, setType] = useState("Patent filing");

  return (
    <div className="surface-cream min-h-screen" data-testid="contact-page">
      <Header/>

      {/* HERO */}
      <section className="bg-white border-b border-[#E1E8E2] py-14 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] blob bg-[#7CC242]"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 text-center">
          <div className="chip inline-flex mb-4">Contact</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#0A2520] mb-4 leading-tight">Let's talk about your idea.</h1>
          <p className="text-lg text-[#5A6B66] max-w-2xl mx-auto">Whether you're filing your first patent or your hundredth, we're available across phone, email, WhatsApp, and in person at three Indian offices.</p>
        </div>
      </section>

      {/* QUICK CONTACT */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 -mt-8 mb-12 relative z-10">
        <div className="grid md:grid-cols-3 gap-4">
          <QuickCard icon={Phone} title="Call us" line1={BRAND.phone} line2="Mon-Sat · 10AM-7PM IST" cta="Call now" link={`tel:${BRAND.phone}`}/>
          <QuickCard icon={Mail} title="Email" line1={BRAND.email} line2="Avg. response in 4 hours" cta="Send email" link={`mailto:${BRAND.email}`}/>
          <QuickCard icon={MessageCircle} title="WhatsApp" line1={BRAND.whatsapp} line2="Quickest response · 24×7" cta="Chat on WhatsApp" link="#"/>
        </div>
      </section>

      {/* FORM + SIDEBAR */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-[1.3fr_1fr] gap-10">
        {/* Form */}
        <div className="card-flat p-8 lg:p-10" data-testid="contact-form">
          <div className="chip mb-3">Send us a note</div>
          <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-2">We read every message.</h2>
          <p className="text-[#5A6B66] mb-8">Real humans, real responses. Avg. reply time 4 business hours.</p>

          {/* Inquiry type chips */}
          <div className="mb-7">
            <label className="block font-display text-sm font-semibold text-[#0A2520] mb-3">I'm reaching out about</label>
            <div className="flex flex-wrap gap-2">
              {INQUIRY_TYPES.map((t) => (
                <button key={t} onClick={() => setType(t)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition ${type === t ? "bg-[#0E6E5E] text-white" : "bg-[#EBF3EC] text-[#0B5447] hover:bg-[#D5E2D6]"}`}
                  data-testid={`inquiry-${t}`}>{t}</button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-5">
            <div>
              <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Full name</label>
              <input placeholder="Priya Ramaswamy" className="input" data-testid="contact-name"/>
            </div>
            <div>
              <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Company / Organisation</label>
              <input placeholder="BioGenix Labs" className="input" data-testid="contact-company"/>
            </div>
            <div>
              <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Work email</label>
              <input placeholder="priya@biogenix.com" type="email" className="input" data-testid="contact-email"/>
            </div>
            <div>
              <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Phone</label>
              <input placeholder="+91 99999 88888" type="tel" className="input" data-testid="contact-phone"/>
            </div>
          </div>

          <div className="mb-5">
            <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Your role</label>
            <select className="input" data-testid="contact-role">
              <option>Founder / CEO</option>
              <option>IP Head / Counsel</option>
              <option>R&D / Engineering</option>
              <option>Academic / Researcher</option>
              <option>Student</option>
              <option>Other</option>
            </select>
          </div>

          <div className="mb-7">
            <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Your message</label>
            <textarea rows={5} placeholder="Tell us what you're working on, what you've tried so far, and what you're hoping we can help with."
                      className="input resize-none leading-relaxed" data-testid="contact-message"/>
          </div>

          <label className="flex items-start gap-2.5 mb-6 text-sm text-[#5A6B66] cursor-pointer">
            <input type="checkbox" defaultChecked className="mt-1 accent-[#0E6E5E]"/>
            <span>I agree to Udhyak's privacy policy and consent to being contacted by phone / email / WhatsApp.</span>
          </label>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button className="btn-primary" data-testid="contact-submit"><Send size={14}/> Send message</button>
            <div className="text-xs text-[#5A6B66] flex items-center gap-1.5">
              <Clock size={12}/> Avg. response: 4 business hours
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          <div className="card p-7 surface-deep text-white border-[#0A2520]">
            <div className="chip chip-accent mb-3"><CalendarClock size={12}/> Prefer a call?</div>
            <h3 className="font-display font-bold text-2xl mb-2 leading-tight">Book a 30-min consultation.</h3>
            <p className="text-white/70 text-sm leading-relaxed mb-5">Free, no commitment, with a senior IP advisor in your preferred language.</p>
            <button className="btn-accent text-sm w-full justify-center" data-testid="contact-schedule-cta">Schedule a Free Call <ArrowRight size={14}/></button>
          </div>

          <div className="card p-7">
            <div className="chip mb-3">Our offices</div>
            <h3 className="font-display font-bold text-xl text-[#0A2520] mb-5">Drop by in person.</h3>
            <div className="space-y-5">
              {BRAND.offices.map((o) => (
                <div key={o.city} className="flex gap-3 pb-5 border-b border-[#F0F4F0] last:border-0 last:pb-0">
                  <div className="w-10 h-10 rounded-lg surface-sage flex items-center justify-center text-[#0E6E5E] flex-shrink-0">
                    <MapPin size={16}/>
                  </div>
                  <div>
                    <div className="font-display font-bold text-[#0A2520] mb-1">{o.city}</div>
                    <p className="text-sm text-[#5A6B66] leading-relaxed mb-1">{o.addr}</p>
                    <p className="text-xs text-[#5A6B66] flex items-center gap-1"><Clock size={11}/> {o.hours}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Map placeholder */}
          <div className="card p-0 overflow-hidden surface-sage h-56 relative flex items-center justify-center">
            <div className="absolute inset-0 grid-bg opacity-50"/>
            <div className="relative text-center">
              <MapPin size={28} className="mx-auto text-[#0E6E5E] mb-2"/>
              <div className="font-display font-bold text-[#0A2520]">3 offices · PAN India</div>
              <div className="text-sm text-[#5A6B66] mt-1">Delhi · Mumbai · Bengaluru</div>
            </div>
          </div>
        </aside>
      </section>

      <Footer/>
    </div>
  );
}

function QuickCard({ icon: Icon, title, line1, line2, cta, link }) {
  return (
    <a href={link} className="card p-6 block">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl surface-sage flex items-center justify-center text-[#0E6E5E] flex-shrink-0">
          <Icon size={20}/>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-[#5A6B66] font-semibold mb-1">{title}</div>
          <div className="font-display font-bold text-[#0A2520]">{line1}</div>
          <div className="text-xs text-[#5A6B66] mt-1">{line2}</div>
          <div className="text-sm text-[#0E6E5E] font-semibold mt-3 inline-flex items-center gap-1">
            {cta} <ArrowRight size={12}/>
          </div>
        </div>
      </div>
    </a>
  );
}
